<script setup lang="ts">
import { useI18n } from 'vue-i18n'

import type { RuleDTO } from '@/types'
import type { WorkspaceDTO, WorkspaceRuleListItemDTO } from '@/types/workspace'

import RuleFormDialog from '@/components/rules/RuleFormDialog.vue'
import RulesTable from '@/components/rules/RulesTable.vue'
import { appConfirm } from '@/composables/useAppConfirm'
import { useAppToast } from '@/composables/useAppToast'
import ruleService from '@/services/ruleService'

const props = defineProps<{
  workspace: WorkspaceDTO
}>()

const emit = defineEmits<{
  (e: 'reloadWorkspace'): void
}>()

definePage({
  meta: {
    requiresAuth: true,
    requiresInternalAccess: true,
  },
})

const { t } = useI18n()
const toast = useAppToast()

// Workspace ID from loaded workspace
const workspaceId = computed(() => props.workspace.id)

// Dialog state
const showRuleDialog = ref(false)
const selectedRule = ref<RuleDTO | null>(null)

const sortedRules = computed(() => [...(props.workspace.rules ?? [])].sort((a, b) => b.id - a.id))

function createRule() {
  selectedRule.value = null
  showRuleDialog.value = true
}

async function editRule(rule: WorkspaceRuleListItemDTO) {
  selectedRule.value = await ruleService.getById(rule.id)
  showRuleDialog.value = true
}

function onRuleSaved() {
  showRuleDialog.value = false
  selectedRule.value = null
  emit('reloadWorkspace')
}

async function confirmDelete(rule: WorkspaceRuleListItemDTO) {
  // Check if rule is in use
  if (rule.eventCount > 0 || rule.groupCount > 0) {
    toast.add({
      color: 'warning',
      title: t('common.messages.warning'),
      description: t('features.rules.cannotDeleteInUse', {
        events: rule.eventCount,
        groups: rule.groupCount,
      }),
      duration: 5000,
    })
    return
  }

  const accepted = await appConfirm({
    message: t('rules.confirmDelete', { name: rule.name }),
    title: t('common.labels.confirmDeletion'),
    cancelLabel: t('common.actions.cancel'),
    confirmLabel: t('common.actions.delete'),
    confirmColor: 'error',
  })
  if (accepted) await deleteRule(rule.id)
}

async function deleteRule(id: number) {
  try {
    await ruleService.delete(id)
    toast.add({
      color: 'success',
      title: t('common.messages.success'),
      description: t('features.rules.deleteSuccess'),
      duration: 3000,
    })
    emit('reloadWorkspace')
  } catch (err) {
    console.error('Error deleting rule:', err)
    toast.add({
      color: 'error',
      title: t('common.messages.error'),
      description: t('features.rules.deleteError'),
      duration: 3000,
    })
  }
}
</script>

<template>
  <div class="rules-page">
    <!-- Header with Create Button -->
    <div class="mb-6 flex items-center justify-between">
      <div>
        <h2 class="text-2xl font-bold text-gray-900 dark:text-white">
          {{ t('features.rules.title') }}
        </h2>
        <p class="mt-1 text-gray-600 dark:text-gray-300">
          {{ t('features.rules.description') }}
        </p>
      </div>
      <UButton
        icon="i-lucide-plus"
        :label="t('features.rules.createNew')"
        color="neutral"
        @click="createRule"
      />
    </div>

    <RulesTable :rules="sortedRules" @edit="editRule" @delete="confirmDelete" />

    <!-- Create/Edit Rule Dialog -->
    <RuleFormDialog
      :visible="showRuleDialog"
      :rule="selectedRule"
      :workspace-id="workspaceId"
      @update:visible="showRuleDialog = $event"
      @success="onRuleSaved"
    />
  </div>
</template>
